<template>
  <div class="sls-slider">
    <slot name="slider-top" />
    <v-slider
      v-model="choiceSelected"
      :class="cssClasses"
      show-ticks="always"
      :max="maxValue"
      step="1"
      track-size="2"
      :ticks="ticksLabels"
      :disabled="disabled"
      @end="handleValueChanged"
      @click="handleClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { isNull } from "@utils/var.check";

// Props
interface Props {
  choice?: number | null;
  ticksLabels?: Record<number, string>;
  max: number;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  choice: null,
  ticksLabels: () => ({}),
  disabled: false,
});

const emit = defineEmits<{
  changed: [value: number];
  click: [event: MouseEvent];
}>();

// Data
const choiceSelected = ref<number | undefined>(props.choice ?? undefined);

// Computed
const maxValue = computed(() => {
  // Defaulted when max is not provided
  return props.max ? props.max : Object.keys(props.ticksLabels).length - 1;
});

const cssClasses = computed(() => {
  return {
    "is-thumb-hidden": isNull(choiceSelected.value),
  };
});

// Methods
const handleValueChanged = (value: number) => {
  emit("changed", value);
};

const handleClick = (event: MouseEvent) => {
  emit("click", event);
};

// Watch
watch(
  () => props.choice,
  (value) => {
    choiceSelected.value = value ?? undefined;
  },
  { immediate: true },
);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/spacing";

.sls-slider {
  display: flex;
  align-items: center;
  width: 100%;
  margin: auto;

  .is-thumb-hidden {
    .v-slider-thumb {
      display: none;
    }
  }
  .v-slider-track__fill {
    display: none;
  }
}
</style>
